import type {
  AtcProduct,
  AtcProductCarouselImage,
  ImageAreaItem,
  WelcomeKitData,
} from "@/types/atc";
import type { PromoData } from "@/types/promoData";
import { isLpMonthlyInBrowser } from "@/utils/lpMonthly";

/**
 * Start Strong Set welcome kit on Men/Women core PDPs, booster duos, and kit PDPs.
 * Set to false to hide the kit even when no promo is running.
 */
export const useWelcomeKit = false;

export const welcomeKitDisclaimer =
  "‡Free gift with purchase of 1, 3 or 6-month Nutraceutical subscription. Expires 11/5/2026 at 11:59pm PST. Valid only for new nutrafol.com subscribers age 18 or older. Free travel-size shampoo and conditioner and 7-day pill organizer (approx. $46 value) are one-time delivery only, non-refundable and not otherwise exchangeable for cash or product. Cannot be combined with other offers. While supplies last. Void where prohibited. By participating, you agree to Nutrafol’s <a href='/our/brand/support/legal/terms-of-use/' target='_blank'>Terms of Use</a>.<br/><br/>";

export const menWelcomeKitDisclaimer =
  "‡Free gift with purchase of 1, 3 or 6-month Nutraceutical subscription. Expires 11/5/2026 at 11:59pm PST. Valid only for new nutrafol.com subscribers age 18 or older. Free travel-size 2-in-1 Shampoo and Conditioner and 7-day pill organizer (approx. $33 value) are one-time delivery only, non-refundable and not otherwise exchangeable for cash or product. Cannot be combined with other offers. While supplies last. Void where prohibited. By participating, you agree to Nutrafol’s <a href='/our/brand/support/legal/terms-of-use/' target='_blank'>Terms of Use</a>.<br/><br/>";

/** Shared Start Strong Set fields. Pass page-specific carousel and image-area arrays. */
export function womenWelcomeKitData(
  welcomeKitCarouselImages: AtcProductCarouselImage[],
  welcomeKitImageArea: ImageAreaItem[]
): WelcomeKitData {
  return {
    topBanner: {
      tag: "Start Stronger Set",
      copy: "The support you need for a strong start to your journey—<span class='whitespace-nowrap'><b>FREE when you subscribe.</b><sup>‡</sup></span>",
    },
    welcomeKitCarouselImages,
    welcomeKitImageArea,
    gwpBanner: {
      img: "https://images.ctfassets.net/0rbfqd9c4jdo/5j9BKUxDkEbcDV2VWZcs5c/f3c2ee02f849c178d04a84d9fde6bdb3/welcome-kit-gwp-banner-var-a_masterbrand.jpg",
      title: "Start Stronger Set<sup>‡</sup>",
      value: "<span class='line-through'>$46</span> <b>FREE</b>",
      listItems: [
        { text: "7-Day Pill Organizer" },
        { text: "Travel-Size Shampoo" },
        { text: "Travel-Size Conditioner" },
      ],
    },
  };
}

/** Shared Start Strong Set fields for men’s PDPs. Pass page-specific carousel and image-area arrays. */
export function menWelcomeKitData(
  welcomeKitCarouselImages: AtcProductCarouselImage[],
  welcomeKitImageArea: ImageAreaItem[]
): WelcomeKitData {
  return {
    topBanner: {
      tag: "Start Stronger Set",
      copy: "The support you need for a strong start to your journey—<span class='whitespace-nowrap'><b>FREE when you subscribe.</b><sup>‡</sup></span>",
    },
    welcomeKitCarouselImages,
    welcomeKitImageArea,
    gwpBanner: {
      img: "https://images.ctfassets.net/0rbfqd9c4jdo/VTz041hfRSPZ3qritP4Km/8f2af5adf88a787e2f2c43b44c844a23/welcome-kit-gwp-banner-var-a_men.jpg",
      title: "Start Stronger Set<sup>‡</sup>",
      value: "<span class='line-through'>$33</span> <b>FREE</b>",
      listItems: [
        { text: "7-Day Pill Organizer" },
        { text: "Travel-Size 2-in-1 Shampoo and Conditioner" },
      ],
    },
  };
}

export function resolveWelcomeKitCarouselImages(
  product: AtcProduct | undefined,
  showWelcomeKit: boolean
): AtcProductCarouselImage[] | undefined {
  if (showWelcomeKit && product?.welcomeKitData?.welcomeKitCarouselImages?.length) {
    return product.welcomeKitData.welcomeKitCarouselImages;
  }
  return product?.carouselImages;
}

export function resolveWelcomeKitImageArea(
  product: AtcProduct | undefined,
  showWelcomeKit: boolean
): ImageAreaItem[] | undefined {
  if (showWelcomeKit && product?.welcomeKitData?.welcomeKitImageArea?.length) {
    return product.welcomeKitData.welcomeKitImageArea;
  }
  return product?.imageArea;
}

/**
 * PDP purchase_source values used while the Start Strong Set is active.
 * Mapped from the default core PDP sources so promo traffic keeps the original.
 */
const WELCOME_KIT_PURCHASE_SOURCES: Record<string, string> = {
  women_pdp: "advanced_pdp_startstrongkit",
  balance_pdp: "balance_pdp_startstrongkit",
  postpartum_pdp: "postpartum_pdp_startstrongkit",
  mens_pdp: "mens_pdp_startstrongkit",
  mens50_pdp: "men50_pdp_startstrongkit",
  duo_pdp: "duo_pdp_startstrongkit",
  fullest_hair_kit_lp: "fullest_hair_kit_lp_startstrongkit",
  mens_duo_hgn_plus_active_cleanse: "mens_duo_hgn_plus_active_cleanse_startstrongkit",
};

/**
 * Subscription adds use the Start Strong kit source. One-time adds keep the original.
 * Monthly PDP LPs append `_monthly_lp` (NT2-32584).
 */
const MONTHLY_LP_PURCHASE_SOURCE_SFX = "_monthly_lp";

export function resolveWelcomeKitPurchaseSource(
  purchaseSource: string,
  showWelcomeKit: boolean,
  isSubscription = true,
  lpMonthly?: boolean
): string {
  const mapped =
    !showWelcomeKit || !isSubscription
      ? purchaseSource
      : (WELCOME_KIT_PURCHASE_SOURCES[purchaseSource] ?? purchaseSource);
  const isMonthly = lpMonthly ?? isLpMonthlyInBrowser();
  if (!isMonthly || mapped.endsWith(MONTHLY_LP_PURCHASE_SOURCE_SFX)) {
    return mapped;
  }
  return mapped + MONTHLY_LP_PURCHASE_SOURCE_SFX;
}

export function applyWelcomeKitToProduct(
  product: AtcProduct | undefined,
  promoCf: PromoData | null | undefined
): { product: AtcProduct | undefined; showWelcomeKit: boolean } {
  const hideWelcomeKit = promoCf && promoCf?.savingsData?.web?.useWelcomeKitCf !== true;
  const showWelcomeKit = Boolean(useWelcomeKit && !hideWelcomeKit && product?.welcomeKitData);
  return { product, showWelcomeKit };
}

export function applyWelcomeKitToProducts(
  products: AtcProduct[],
  promoCf: PromoData | null | undefined
): { products: AtcProduct[]; showWelcomeKit: boolean } {
  const hideWelcomeKit = promoCf && promoCf?.savingsData?.web?.useWelcomeKitCf !== true;
  const showWelcomeKit = Boolean(useWelcomeKit && !hideWelcomeKit && products[0]?.welcomeKitData);
  if (!showWelcomeKit) {
    return { products, showWelcomeKit };
  }
  return {
    showWelcomeKit,
    products: products.map((product) => ({
      ...product,
      carouselImages: resolveWelcomeKitCarouselImages(product, true) ?? product.carouselImages,
      imageArea: resolveWelcomeKitImageArea(product, true) ?? product.imageArea,
    })),
  };
}

/** Pages wrap the disclaimer in their own breaks, and the disclaimer ends with a pair too. */
function collapseExtraBreaks(line: string): string {
  return line.replace(/(?:<br\s*\/?>\s*){3,}/gi, "<br/><br/>");
}

export function buildWelcomeKitReferenceLine(
  line: string,
  promoCf: PromoData | null | undefined
): string {
  const hideWelcomeKit = promoCf && promoCf?.savingsData?.web?.useWelcomeKitCf !== true;
  const showWelcomeKit = useWelcomeKit && !hideWelcomeKit;
  const referenceLine = showWelcomeKit
    ? line
    : line.replace(welcomeKitDisclaimer, "").replace(menWelcomeKitDisclaimer, "");
  const combined = [promoCf?.tc ? promoCf.tc : null, referenceLine]
    .filter(Boolean)
    .join("<br/><br/>");
  return showWelcomeKit ? combined : collapseExtraBreaks(combined);
}
